<template>
  <div
    v-if="open"
    class="picker-backdrop"
    @click.self="$emit('close')"
    @keydown.esc.prevent="$emit('close')"
  >
    <section
      class="collection-picker"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-picker-heading"
    >
      <header>
        <div>
          <p>{{ momentPending ? 'Save highlight' : 'Active collection' }}</p>
          <h3 id="collection-picker-heading">
            {{ momentPending ? 'Where should this moment go?' : 'Choose a collection' }}
          </h3>
        </div>
        <button
          ref="closeButton"
          type="button"
          class="close-button"
          aria-label="Close collection chooser"
          :disabled="pending"
          @click="$emit('close')"
        >×</button>
      </header>

      <div v-if="collections.length" class="existing-collections">
        <p>Your collections</p>
        <button
          v-for="collection in collections"
          :key="collection.collection_id"
          type="button"
          :class="{ selected: collection.collection_id === activeCollectionId }"
          :disabled="pending"
          @click="$emit('select', collection.collection_id)"
        >
          <span>
            <strong>{{ collection.title || 'Saved highlights' }}</strong>
            <small>
              {{ collection.item_count || 0 }} moment{{ collection.item_count === 1 ? '' : 's' }}
            </small>
          </span>
          <span aria-hidden="true">
            {{ collection.collection_id === activeCollectionId ? '✓' : '→' }}
          </span>
        </button>
      </div>

      <form class="new-collection" @submit.prevent="createCollection">
        <div>
          <p>{{ collections.length ? 'Or start something new' : 'Start your first collection' }}</p>
          <label for="new-collection-title">Collection name</label>
        </div>
        <div class="create-row">
          <input
            id="new-collection-title"
            ref="titleInput"
            v-model="title"
            maxlength="100"
            autocomplete="off"
            :disabled="pending"
          />
          <button type="submit" :disabled="pending || !title.trim()">
            {{ pending ? 'Working…' : (momentPending ? 'Create & save' : 'Create') }}
          </button>
        </div>
      </form>

      <p v-if="error" class="picker-error" role="alert">{{ error }}</p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'CeremonyCollectionPicker',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    collections: {
      type: Array,
      default: () => [],
    },
    activeCollectionId: {
      type: String,
      default: '',
    },
    defaultTitle: {
      type: String,
      default: '',
    },
    momentPending: {
      type: Boolean,
      default: false,
    },
    pending: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  data() {
    return { title: '' }
  },
  watch: {
    open(value) {
      if (!value) return
      this.title = this.defaultTitle
      this.$nextTick(() => {
        const target = this.collections.length ? this.$refs.closeButton : this.$refs.titleInput
        target?.focus()
        if (!this.collections.length) this.$refs.titleInput?.select()
      })
    },
  },
  methods: {
    createCollection() {
      const title = String(this.title || '').trim()
      if (title && !this.pending) this.$emit('create', title)
    },
  },
}
</script>

<style scoped>
.picker-backdrop {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15, 20, 33, 0.52);
  backdrop-filter: blur(5px);
}

.collection-picker {
  width: min(100%, 560px);
  max-height: min(760px, calc(100vh - 48px));
  padding: 24px;
  overflow-y: auto;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 20, 33, 0.3);
}

.collection-picker header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.collection-picker p {
  margin: 0 0 6px;
  color: #7c3aed;
  font-size: 0.68rem;
  font-weight: 760;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.collection-picker h3 {
  margin: 0;
  color: #171c2a;
  font-size: 1.35rem;
  letter-spacing: -0.025em;
}

.close-button {
  width: 42px;
  height: 42px;
  border: 1px solid #e0e3e9;
  border-radius: 50%;
  background: #fff;
  color: #5f6675;
  font-size: 1.35rem;
  cursor: pointer;
}

.existing-collections,
.new-collection {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #e8eaf0;
}

.existing-collections > button {
  display: flex;
  width: 100%;
  min-height: 62px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 8px;
  padding: 10px 14px;
  border: 1px solid #e0e3e9;
  border-radius: 12px;
  background: #fff;
  color: #343a49;
  text-align: left;
  cursor: pointer;
}

.existing-collections > button:hover,
.existing-collections > button.selected {
  border-color: #bba8f1;
  background: #f8f6ff;
  color: #5b21b6;
}

.existing-collections button span:first-child {
  display: grid;
  gap: 4px;
}

.existing-collections small {
  color: #777e8e;
  font-size: 0.7rem;
}

.new-collection label {
  color: #4e5666;
  font-size: 0.74rem;
  font-weight: 680;
}

.create-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 9px;
  margin-top: 7px;
}

.create-row input {
  min-width: 0;
  min-height: 46px;
  padding: 0 13px;
  border: 1px solid #cfd4dd;
  border-radius: 10px;
  font: inherit;
}

.create-row button {
  min-height: 46px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: #6d28d9;
  color: #fff;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 720;
  cursor: pointer;
}

button:disabled {
  opacity: 0.55;
  cursor: default;
}

.picker-error {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #fff3f1;
  color: #b42318;
  font-size: 0.74rem;
  letter-spacing: 0;
  line-height: 1.45;
  text-transform: none;
}

@media (max-width: 560px) {
  .picker-backdrop { padding: 14px; }
  .collection-picker { padding: 20px 16px; }
  .create-row { grid-template-columns: 1fr; }
}
</style>
